import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Target, Eye, Users, Award, Heart, Sparkles, Shield, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic core values
const iconMap = {
  Heart: Heart,
  Shield: Shield,
  Users: Users,
  Award: Award,
  Sparkles: Sparkles,
  Zap: Zap,
  Target: Target,
  Eye: Eye
};

export const About = () => {
  const [content, setContent] = useState({});
  const [coreValues, setCoreValues] = useState([]);

  useEffect(() => {
    fetchContent();
    fetchCoreValues();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const fetchCoreValues = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/core-values`);
      setCoreValues(response.data.filter(v => v.is_active));
    } catch (error) {
      console.error('Error fetching core values:', error);
    }
  };

  // Default team image
  const teamImage = content.about_team_image || 'https://images.unsplash.com/photo-1682962232755-f1d051ddb638?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxncm91cCUyMG9mJTIwaGFwcHklMjBpbmRpYW4lMjBmcmllbmRzJTIwc3R1ZGVudHMlMjB0YWxraW5nJTIwaW4lMjBtb2Rlcm4lMjBjYWZlJTIwb3IlMjBsb3VuZ2V8ZW58MHx8fHwxNzY5MDc0MDg5fDA&ixlib=rb-4.1.0&q=85';

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              About <span className="text-brand-purple">INDWELLER STAY</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-3xl mx-auto">
              Your trusted partner in finding the perfect accommodation solution
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={teamImage}
                alt="Community"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-video"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl font-semibold text-gray-900 mb-6">
                Who We Are
              </h2>
              <p className="text-gray-600 font-body leading-relaxed mb-4">
                {content.about_intro_para1 || 'INDWELLER STAY is a premier Paying Guest and Home Stay accommodation provider in India, dedicated to offering comfortable, safe, and affordable living spaces for students and working professionals.'}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-4">
                {content.about_intro_para2 || 'With multiple properties across prime locations, we understand the challenges of finding the perfect place to stay. Our mission is to make this process seamless while ensuring you feel right at home from day one.'}
              </p>
              <p className="text-gray-600 font-body leading-relaxed">
                {content.about_intro_para3 || 'We take pride in creating not just living spaces, but vibrant communities where residents can thrive, grow, and build lasting connections.'}
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl border border-gray-100"
              data-testid="vision-card"
            >
              <div className="w-14 h-14 bg-brand-purple/10 rounded-xl flex items-center justify-center mb-6">
                <Eye className="text-brand-purple" size={28} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600 font-body leading-relaxed">
                {content.about_vision || 'To become India\'s most trusted and preferred accommodation provider, known for exceptional quality, transparent operations, and creating homes away from home for every resident.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-2xl border border-gray-100"
              data-testid="mission-card"
            >
              <div className="w-14 h-14 bg-brand-orange/10 rounded-xl flex items-center justify-center mb-6">
                <Target className="text-brand-orange" size={28} />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-gray-600 font-body leading-relaxed">
                {content.about_mission || 'To provide safe, comfortable, and well-equipped living spaces with excellent amenities at affordable prices, while fostering a supportive community environment that helps our residents succeed in their personal and professional lives.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
              {content.about_values_title || 'Our Core Values'}
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-2xl mx-auto">
              {content.about_values_subtitle || 'The principles that guide everything we do'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {coreValues.map((value, index) => {
              const IconComponent = iconMap[value.icon] || Heart;
              return (
                <motion.div
                  key={value.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gray-50 p-8 rounded-2xl hover:shadow-[0_8px_16px_rgba(91,45,139,0.08)] transition-all duration-300"
                  data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-14 h-14 bg-brand-purple/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="text-brand-purple" size={28} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 font-body">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};
